import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SocialShare from '../components/SocialShare';
import MetaTags from '../components/MetaTags';
import api from '../api/axios';
import './EventDetailPage.css';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

function EventBody({ description }) {
    if (!description) return <p>For more information or inquiries, please contact us at <a href="mailto:mncwestv@gmail.com">mncwestv@gmail.com</a>.</p>;
    const isHtml = description.includes('<');
    if (isHtml) return <div className="event-details-content" dangerouslySetInnerHTML={{ __html: description }} />;
    return <div className="event-details-content">{description.split('\n').map((line, i) => <p key={i}>{line}</p>)}</div>;
}

const EventDetailPage = () => {
    const { id } = useParams();
    const [site, setSite] = useState(null);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/content/site'),
            api.get(`/events/${id}`),
        ])
            .then(([siteRes, eventRes]) => {
                setSite(siteRes.data);
                setEvent(eventRes.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error loading event:', error);
                setLoading(false);
            });
    }, [id]);

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="error-screen">
                <h1>Event not found</h1>
                <Link to="/events" className="btn btn-primary">Back to Events</Link>
            </div>
        );
    }

    const pageUrl = `${window.location.origin}/events/${id}`;
    const excerpt = event.description ? event.description.replace(/<[^>]+>/g, '').substring(0, 160) : '';

    return (
        <div className="App">
            <MetaTags
                title={`${event.title} - Melbourne Nepalese Community West`}
                description={excerpt}
                url={pageUrl}
                image={event.image_url}
                type="article"
            />

            <Header data={site} />
            <div className="event-detail-container">
                <div className="container">
                    <Link to="/events" className="back-link">
                        <i className="fas fa-arrow-left"></i> Back to Events
                    </Link>

                    <article className="event-detail">
                        <div className="event-detail-content">
                            <div className="event-main-content">
                                <div className="event-left-content">
                                    <div className="event-detail-header">
                                        <div className="event-status-badge-large">
                                            {event.status === 'upcoming' ? (
                                                <span className="upcoming">Upcoming Event</span>
                                            ) : (
                                                <span className="past">Past Event</span>
                                            )}
                                        </div>
                                        <h1>{event.title}</h1>
                                        <div className="event-meta">
                                            <div className="event-meta-item">
                                                <i className="fas fa-calendar"></i>
                                                <span>{fmtDate(event.event_date)}</span>
                                            </div>
                                            {event.venue && (
                                                <div className="event-meta-item">
                                                    <i className="fas fa-map-marker-alt"></i>
                                                    <span>{event.venue}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {event.image_url && (
                                        <div className="event-image-container">
                                            <img src={event.image_url} alt={event.title} className="event-detail-image" />
                                        </div>
                                    )}

                                    <SocialShare
                                        url={pageUrl}
                                        title={event.title}
                                        description={excerpt}
                                    />

                                    <div className="event-body">
                                        <h3>About This Event</h3>
                                        <EventBody description={event.description} />
                                    </div>
                                </div>

                                <div className="event-sidebar">
                                    <div className="event-info-card">
                                        <h3>Event Details</h3>
                                        <div className="event-info-item">
                                            <i className="fas fa-clock"></i>
                                            <div className="event-info-item-content">
                                                <strong>Time</strong>
                                                <span>{event.time || 'To be announced'}</span>
                                            </div>
                                        </div>
                                        <div className="event-info-item">
                                            <i className="fas fa-hourglass-half"></i>
                                            <div className="event-info-item-content">
                                                <strong>Duration</strong>
                                                <span>Approximately 3-4 hours</span>
                                            </div>
                                        </div>
                                        <div className="event-info-item">
                                            <i className="fas fa-ticket-alt"></i>
                                            <div className="event-info-item-content">
                                                <strong>Entry</strong>
                                                <span>Free for members</span>
                                            </div>
                                        </div>
                                        <div className="event-info-item">
                                            <i className="fas fa-user-check"></i>
                                            <div className="event-info-item-content">
                                                <strong>Registration</strong>
                                                <span>Required (limited seats)</span>
                                            </div>
                                        </div>
                                        {event.status === 'upcoming' && event.registerUrl && (
                                            <div className="event-register-button">
                                                <a href={event.registerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                                                    <i className="fas fa-ticket-alt"></i> Register Now
                                                </a>
                                            </div>
                                        )}
                                        {event.photoAlbumUrl && event.photoAlbumUrl.trim() !== '' && (
                                            <div className="event-register-button" style={{ marginTop: event.status === 'upcoming' ? '10px' : '0' }}>
                                                <a href={event.photoAlbumUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                                                    <i className="fab fa-facebook me-2"></i>See Photos
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
            <Footer data={site} />
        </div>
    );
};

export default EventDetailPage;
